/**
 * Deterministic array shuffle seeded by a string. Used so the token pool
 * for a `translate_tap` exercise is stable across re-renders — if we
 * re-shuffled on every render the chips would jump around as the user
 * interacts.
 *
 * Algorithm: xmur3 → seeded mulberry32 → Fisher–Yates. Same seed always
 * produces the same permutation.
 */

function xmur3(seed: string): () => number {
  let h = 1779033703 ^ seed.length;
  for (let i = 0; i < seed.length; i++) {
    h = Math.imul(h ^ seed.charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  return () => {
    h = Math.imul(h ^ (h >>> 16), 2246822507);
    h = Math.imul(h ^ (h >>> 13), 3266489909);
    h ^= h >>> 16;
    return h >>> 0;
  };
}

function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Uniform [0,1) generator, deterministic per seed string. For consumers
 *  that need a raw random stream (e.g. weighted draws) rather than a
 *  finished shuffle. */
export function createRng(seed: string): () => number {
  return mulberry32(xmur3(seed)());
}

export function seededShuffle<T>(items: readonly T[], seed: string): T[] {
  const rng = createRng(seed);
  const copy = items.slice();
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

/** One entry in a tap-pairs column. `pairIndex` is the stable authored index
 *  of the pair (used to score matches); `text` is what's shown. */
export interface MatchEntry {
  pairIndex: number;
  text: string;
}

/**
 * Build the two columns for a tap-pairs (matching) exercise so the layout
 * always *looks* shuffled — a plain seeded shuffle is statistically fine but
 * deterministic, so a given exercise can land forever on an order that reads
 * as "barely mixed" (e.g. the left column in authored order, or a correct
 * pair sitting on the same row). Tornike flagged exactly that.
 *
 * Two guarantees (when the pair count allows, i.e. ≥ 2):
 *   1. The left column is a derangement of authored order — no English item
 *      stays in its original row, so it never reads as the source list.
 *   2. No correct pair shares a row: for every row i,
 *      right[i].pairIndex ≠ left[i].pairIndex — kills the "diagonal" giveaway
 *      and the aligned-rows look.
 *
 * Deterministic per `seed` (we bump a suffix until the constraints hold, with
 * a hard attempt cap), so columns stay stable across re-renders — chips never
 * jump while the user is matching.
 */
export function shuffleMatchColumns(
  pairs: ReadonlyArray<{ en: string; ka: string }>,
  seed: string,
): { left: MatchEntry[]; right: MatchEntry[] } {
  const baseLeft: MatchEntry[] = pairs.map((p, i) => ({ pairIndex: i, text: p.en }));
  const baseRight: MatchEntry[] = pairs.map((p, i) => ({ pairIndex: i, text: p.ka }));
  if (pairs.length < 2) return { left: baseLeft, right: baseRight };

  const MAX_ATTEMPTS = 64;

  // 1. Left: deranged vs authored order (nothing in its original slot).
  let left = baseLeft;
  for (let a = 0; a < MAX_ATTEMPTS; a++) {
    const cand = seededShuffle(baseLeft, `${seed}:en:${a}`);
    left = cand;
    if (cand.every((e, i) => e.pairIndex !== i)) break;
  }

  // 2. Right: no correct pair on the same row as the chosen left column.
  let right = baseRight;
  for (let a = 0; a < MAX_ATTEMPTS; a++) {
    const cand = seededShuffle(baseRight, `${seed}:ka:${a}`);
    right = cand;
    if (cand.every((e, i) => e.pairIndex !== left[i].pairIndex)) break;
  }

  return { left, right };
}
